import { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, useLoaderData, useSearchParams } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { Combobox } from '#app/components/ui/combobox'
import { reactionTypes } from '#app/components/ui/reactions.js'
import { usePersistedState } from '#app/hooks/use-persisted-state'
import { buildComparator, dedupeByTemplate } from '#app/utils/build-popularity.js'
import { getBuildsByTag } from '#app/utils/builds.server.js'
import { momentsAgo } from '#app/utils/dateFormat.js'
import { cleanSkillName } from '#app/utils/scripts.helper.js'
import { getServerNameList } from '#app/utils/shards.server.js'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    return [
    { title: `UO Builds by Tag | ${data?.tagName} | UO Addict` },
    { name: 'description', content: `Browse for Ultima Online builds under the tag ${data?.tagName} on ${brandName}` },
]}

export async function loader({ request, params }: LoaderFunctionArgs) {
    const url = new URL(request.url)
    const sort = url.searchParams.get('sort') ?? 'popular'

    const {builds: rawBuilds} = await getBuildsByTag({tagName: params.tagName ?? "Unknown"});
    const serverList = await getServerNameList();

    const comparator = buildComparator(sort)
    const builds = dedupeByTemplate(rawBuilds, comparator).sort(comparator)

    return json({
        tagName: params.tagName ?? "Unknown",
        builds,
        serverList,
        sort,
    });
};

export default function IndexRoute() {
  const { builds, tagName, serverList, sort } = useLoaderData<typeof loader>()
  const [selectedServer, setSelectedServer] = usePersistedState<string | null>('selectedServer', null)
  const [searchParams, setSearchParams] = useSearchParams()

  const filteredBuilds = selectedServer
    ? builds.filter(build => build.server.name === selectedServer)
    : builds

  const handleSort = (newSort: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('sort', newSort)
    setSearchParams(params)
  }

  return (
	<div className="container mx-auto mb-24 mt-4 px-4 md:mb-48 md:mt-6">
		<Link to="/builds" className="text-sm text-muted-foreground underline hover:no-underline">
			&larr; All Builds
		</Link>

		<div className="mt-3 mb-4 md:mt-4 md:mb-6">
			<h1 className="text-2xl font-bold md:text-4xl">
				Builds tagged <span className="rounded-full bg-blue-600/10 px-3 py-1 text-blue-600">{tagName}</span>
			</h1>
			<p className="mt-2 text-sm text-muted-foreground md:text-base">
				{filteredBuilds.length} build{filteredBuilds.length !== 1 ? 's' : ''} found. <Link to={`/builds/new`} className="underline hover:no-underline">Add your own</Link>
			</p>
		</div>

		<div className="mb-6 space-y-3 md:space-y-4">
			<div className="max-w-xs">
				<Combobox
					name="server"
					value={selectedServer}
					onChange={setSelectedServer}
					options={serverList}
					label="Filter by Server"
					placeholder="All servers"
				/>
			</div>

			<div className="flex items-center gap-1.5 md:gap-2">
				<span className="text-xs text-muted-foreground md:text-sm">Sort:</span>
				<button
					onClick={() => handleSort('popular')}
					className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors md:px-3 md:py-1 md:text-sm ${
						sort === 'popular'
							? 'bg-blue-600 text-white'
							: 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
					}`}
				>
					Most Popular
				</button>
				<button
					onClick={() => handleSort('recent')}
					className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors md:px-3 md:py-1 md:text-sm ${
						sort === 'recent'
							? 'bg-blue-600 text-white'
							: 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
					}`}
				>
					Last Updated
				</button>
			</div>
		</div>

		<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 md:gap-0">
        {
          filteredBuilds.map((build, index) => {

            let total = 0;
            const buildBreakout = build.build?.split(",").map(x => {
              const skill = x.split("|")
              const skillName = skill[0] ?? "No skill name"
              const skillValue = Number(skill[1]) ?? 0
              total += skillValue
              return { name: skillName, value: skillValue }
            })

            return <div key={`build_${index}`}>
                <Link to={`/builds/${build.server.slug}/${build.slug}`}>
                  <div className="skill-scroll-container">
                        <div className='m-2 md:m-4'>
                            <div className='text-center m-auto min-h-24 md:min-h-32 align-text-bottom'>
                            <img src={build.server.logo} alt={build.server.name} className="max-h-16 md:max-h-24 server-logo m-auto" />
                            <span className="text-xs md:text-sm">by {build.user.username} {momentsAgo(build.createdAt.toString())} ago</span>
                            {build.playerCount > 1 && (
                              <span className="block text-[10px] text-green-600 dark:text-green-400 md:text-xs">{build.playerCount} players use this build</span>
                            )}
                            </div>

                            <div className="skill-container-top relative">
                                <div className='not-input text-xs md:text-sm'>{build.name.length ? build.name : "No build name"}</div>
                                {build.reactionCount > 0 && (
                                    <div className="absolute top-1/2 right-1 -translate-y-1/2 flex gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white md:px-2 md:text-xs">
                                        {reactionTypes
                                            .filter((rt) => (build.reactionBreakdown[rt.type] ?? 0) > 0)
                                            .map((rt) => (
                                                <span key={rt.type} title={rt.label}>
                                                    {rt.icon}{build.reactionBreakdown[rt.type]}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </div>
                            <div className="skill-container skill-container-mid">
                                {buildBreakout?.sort( (a,b) => {
                                    if (a.name > b.name) {
                                        return 1;
                                    } else if (b.name > a.name) {
                                        return -1;
                                    }
                                    return 0;
                                }).map(x => { return <div className="skill-row" key={"selected_" + x.name}>
                                    <span className="name">{cleanSkillName(x.name)}</span> <span className="value">{x.value}</span>
                                    </div>})
                                }
                                <div className="skill-row totals">
                                    <span className="name">Total</span> <span className={total > 720 ? "value invalid" : "value"}>{total}</span>
                                </div>
                            </div>
                            <div className="skill-container-bot">&nbsp;</div>
                        </div>
                      </div>
                    </Link>
                    <div className="text-center mb-2 md:mb-0">
                        {build.taggables.map((taggable) => { return <Link key={`tag_${taggable.tag.name}`} to={`/builds/tags/${taggable.tag.name}`} className="underline hover:no-underline text-xs md:text-sm m-1 md:m-2">{taggable.tag.name}</Link>})}
                    </div>
              </div>
          })
        }
		</div>
	</div>
);
}
